import { useState, useEffect, useCallback } from "react";
import { updatePackage, createPackage, deletePackage as apiDeletePackage, CMSPackageCreate, CMSPackageUpdate } from "@/api/packages";
import { packageCategories, type PackageCategory } from "@/mocks/packagesData";
import { useDataContext, MedicalPackage } from "@/context/DataContext";

export type { MedicalPackage };

/** Derive unique ordered categories from a packages list */
function deriveCategories(pkgs: MedicalPackage[]): PackageCategory[] {
  const seen = new Set<string>();
  for (const pkg of pkgs) {
    if (!seen.has(pkg.category)) {
      seen.add(pkg.category);
    }
  }
  // Keep original order from packageCategories for any that exist
  return packageCategories.filter((c) => seen.has(c));
}

// ─── Public hook (for public-facing pages) ────────────────────────────────────
export function usePublicPackages() {
  const { packages: allPackages, packagesLoading: loading, reloadPackages } = useDataContext();
  
  useEffect(() => {
    reloadPackages();
  }, [reloadPackages]);

  const packages = allPackages.filter((p) => (p as any).is_active !== false && (p as any).status !== "inactive");
  const categories = deriveCategories(packages);
  return { packages, categories, loading };
}

// ─── CMS Admin hook (for CMS admin pages) ────────────────────────────────────
export function useCMSPackages() {
  const {
    packages: globalPackages,
    packagesLoading: loading,
    packagesError: error,
    reloadPackages: reload,
  } = useDataContext();

  const [packages, setPackages] = useState<MedicalPackage[]>(globalPackages);
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [hasChanges, setHasChanges] = useState(false);

  // Keep local editor state in sync with global state changes
  useEffect(() => {
    setPackages(globalPackages);
  }, [globalPackages]);

  const saveOne = useCallback(async (p: MedicalPackage) => {
    setSaveStatus("saving");
    try {
      const apiData = {
        package_id: p.package_id || p.id,
        name: p.name,
        name_en: p.nameEn,
        category: p.category,
        category_en: p.categoryEn,
        price: (p.price !== undefined && p.price !== null && (p.price as any) !== "" && !isNaN(Number(p.price))) ? Number(p.price) : null,
        original_price: (p.originalPrice !== undefined && p.originalPrice !== null && (p.originalPrice as any) !== "" && !isNaN(Number(p.originalPrice))) ? Number(p.originalPrice) : null,
        show_price: p.showPrice !== false,
        badge: p.badge,
        badge_en: p.badgeEn,
        icon: p.icon,
        features: p.features,
        features_en: p.featuresEn,
        accent_color: p.accentColor,
        description: p.description,
        description_en: p.descriptionEn,
        duration: p.duration,
        duration_en: p.durationEn,
        target_audience: p.targetAudience,
        target_audience_en: p.targetAudienceEn,
        preparation: p.preparation,
        includes: p.includes,
        faqs: p.faqs,
        is_active: (p as any).status !== undefined ? (p as any).status === "active" : ((p as any).is_active !== false),
      };
      
      // Update existing using its internal 'id' which matches backend UUID
      if (p.id && !p.id.startsWith("pkg-") && p.id.length > 20) {
        await updatePackage(p.id, apiData as CMSPackageUpdate);
      } else {
        await createPackage(apiData as CMSPackageCreate);
      }
      
      await reload();
      setSaveStatus("saved");
      setHasChanges(false);
      setTimeout(() => setSaveStatus("idle"), 2500);
      return { success: true };
    } catch (err) {
      setSaveStatus("error");
      return { success: false, error: err };
    }
  }, [reload]);

  const remove = useCallback(async (id: string) => {
    setSaveStatus("saving");
    try {
      await apiDeletePackage(id);
      await reload();
      setSaveStatus("saved");
      setHasChanges(false);
      setTimeout(() => setSaveStatus("idle"), 2500);
      return { success: true };
    } catch (err) {
      setSaveStatus("error");
      return { success: false, error: err };
    }
  }, [reload]);

  const save = useCallback(async (data: MedicalPackage[]) => {
    setSaveStatus("saving");
    try {
      for (const p of data) {
        const apiData = {
          package_id: p.package_id || p.id,
          name: p.name,
          name_en: p.nameEn,
          category: p.category,
          category_en: p.categoryEn,
          price: p.price,
          original_price: p.originalPrice,
          show_price: p.showPrice !== false,
          badge: p.badge,
          badge_en: p.badgeEn,
          icon: p.icon,
          features: p.features,
          features_en: p.featuresEn,
          accent_color: p.accentColor,
          description: p.description,
          description_en: p.descriptionEn,
          duration: p.duration,
          duration_en: p.durationEn,
          target_audience: p.targetAudience,
          target_audience_en: p.targetAudienceEn,
          preparation: p.preparation,
          includes: p.includes,
          faqs: p.faqs,
        };
        
        if (p.id && !p.id.startsWith("pkg-") && p.id.length > 20) {
          await updatePackage(p.id, apiData as CMSPackageUpdate);
        } else {
          await createPackage(apiData as CMSPackageCreate);
        }
      }
      
      await reload();
      setSaveStatus("saved");
      setHasChanges(false);
      setTimeout(() => setSaveStatus("idle"), 2500);
      return { success: true };
    } catch (err) {
      setSaveStatus("error");
      return { success: false, error: err };
    }
  }, [reload]);

  const updatePackages = useCallback((data: MedicalPackage[]) => {
    setPackages(data);
    setHasChanges(true);
  }, []);

  const reset = useCallback(async () => {
    await reload();
    setHasChanges(false);
  }, [reload]);

  return {
    packages,
    loading,
    error,
    saveStatus,
    hasChanges,
    updatePackages,
    save,
    saveOne,
    remove,
    reset,
    reload,
  };
}
